import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Clock,
  DollarSign,
  CheckCircle,
  Video,
  Calculator,
  MapPin,
  Wifi,
  Globe,
  Lock,
  Calendar
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const OnlinePage = () => {
  const benefits = [
    { 
      title: "Desde cualquier lugar", 
      description: "Accede a tratamiento de calidad sin importar tu ubicación geográfica",
      icon: Globe
    },
    { 
      title: "Flexibilidad total", 
      description: "Horarios adaptados a tu agenda laboral, familiar o de estudios",
      icon: Calendar
    },
    { 
      title: "Menor costo", 
      description: "Sin gastos de traslado ni infraestructura física, más económico",
      icon: DollarSign
    },
    { 
      title: "Privacidad", 
      description: "Tratamiento desde la comodidad y privacidad de tu hogar",
      icon: Lock
    },
    { 
      title: "Continuidad garantizada", 
      description: "Viajes o cambios de ciudad no interrumpen tu tratamiento",
      icon: Wifi
    },
    { 
      title: "Mismo rigor terapéutico", 
      description: "Las técnicas y metodologías son igual de efectivas en formato online",
      icon: Video
    }
  ];

  const idealFor = [
    "Adicciones conductuales (tecnología, juego, compras)",
    "Adicciones leves a moderadas",
    "Personas en zonas sin acceso a centros especializados",
    "Continuidad post-tratamiento residencial",
    "Personas con movilidad reducida",
    "Quienes viajan frecuentemente",
    "Primera consulta o evaluación"
  ];

  const notIdealFor = [
    "Adicciones severas a sustancias",
    "Necesidad de desintoxicación supervisada",
    "Crisis psiquiátricas o riesgo suicida",
    "Entornos de alto riesgo sin posibilidad de aislamiento",
    "Personas sin acceso estable a internet"
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Tratamiento Online para Adicciones",
    "description": "Terapia a distancia mediante videollamada para tratamiento de adicciones. Flexible, accesible y efectiva."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Online", "item": "https://sinadicciones.org/tratamiento/online" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tratamiento Online para Adicciones: Terapia a Distancia | Sin Adicciones Chile</title>
        <meta name="description" content="Tratamiento online para adicciones mediante videollamada. Terapia flexible desde tu hogar. Desde $50.000/sesión. Ideal para adicciones conductuales y leves." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/online" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/online" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">
              Tratamientos
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Online</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                <Video className="w-7 h-7 text-purple-400" />
              </div>
              <div className="px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full">
                <span className="text-purple-400 font-medium">Desde tu hogar</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Tratamiento{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400">
                Online
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Terapia profesional a través de videollamada. 
              Accede a tratamiento de calidad sin salir de casa, 
              con la misma efectividad que la terapia presencial.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <DollarSign className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-white font-bold">$50.000 - $120.000</p>
                <p className="text-slate-400 text-sm">por sesión</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-white font-bold">3-12 meses</p>
                <p className="text-slate-400 text-sm">duración típica</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Calendar className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-white font-bold">1-2 sesiones</p>
                <p className="text-slate-400 text-sm">por semana</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Globe className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-white font-bold">55-65%</p>
                <p className="text-slate-400 text-sm">efectividad</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/profesionales?modality=online"
                className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Profesionales Online
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ventajas del Tratamiento Online
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-purple-500/20"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For / Not Ideal For */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
            ¿Es para ti?
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-emerald-500/10 rounded-2xl border border-emerald-500/30">
              <h3 className="text-xl font-bold text-emerald-400 mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6" />
                Ideal si...
              </h3>
              <ul className="space-y-3">
                {idealFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-red-500/10 rounded-2xl border border-red-500/30">
              <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2">
                <ArrowRight className="w-6 h-6" />
                Considera presencial si...
              </h3>
              <ul className="space-y-3">
                {notIdealFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <ArrowRight className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/tratamiento/ambulatorio"
                className="mt-4 inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
              >
                Ver tratamiento ambulatorio
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-900/50 to-slate-800/50 border border-purple-500/20 p-8 md:p-12 text-center">
            <Video className="w-16 h-16 text-purple-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Comienza tu Tratamiento Online
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Conecta con profesionales especializados que ofrecen atención por videollamada.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/profesionales"
                className="px-8 py-4 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Ver Profesionales
              </Link>
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Tipos de Tratamiento</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Ambulatorio", path: "/tratamiento/ambulatorio", icon: "🏢" },
              { title: "Residencial", path: "/tratamiento/residencial", icon: "🏥" },
              { title: "Desintoxicación", path: "/tratamiento/desintoxicacion", icon: "🏨" },
              { title: "Grupos de Apoyo", path: "/tratamiento/grupos-apoyo", icon: "👥" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-purple-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-purple-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OnlinePage;
